import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  Button,
  EmptyState,
  SkeletonCard,
  TABLE_BODY_SKELETON,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import type { EmptyStateProps } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { formatCreatedOn, formatPrice } from "../../brand/planFormat";
// A deep import rather than the enquiries barrel: the dialog is the same act
// on both screens — pick a plan, discount it, ask for the money — and the
// barrel would drag the enquiry pages in behind it.
import RequestPaymentModal from "../../enquiries/components/RequestPaymentModal";
import type { NewEnquiryPayment } from "../../enquiries/types/enquiries.types";
// The "money is in" question, asked the same way here and on an enquiry — and
// the other answer beside it, along with the two icons that raise them.
import ConfirmPaymentModal from "../../payments/components/ConfirmPaymentModal";
import DeclinePaymentModal from "../../payments/components/DeclinePaymentModal";
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { useClientPayments } from "../hooks/useClientPayments";
import type {
  ClientDetail,
  ClientPayment,
  ClientPaymentStatus,
} from "../types/clients.types";

/** No border on these: they sit inside a row, not beside a page heading. */
const STATUS_STYLES: Record<ClientPaymentStatus, string> = {
  Paid: "bg-[#E7F8EF] text-[#2F8E5B]",
  "Not paid": "bg-[#F1F4F6] text-ink-muted",
  Cancelled: "bg-[#F1F4F6] text-ink-muted",
};

const formatDate = (iso: string): string => formatCreatedOn(new Date(iso));

// The same two cells the plans table is built out of, so the two tabs of one
// screen line up rather than each having its own idea of a row.
const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

interface ClientPaymentsTabProps {
  client: ClientDetail;
  /**
   * Whether this workspace may still put money through, read once by the screen
   * above — see `ClientDetailPage`. Raising a request is plainly one of the
   * things a subscription pays for; settling one is too, since an invoice
   * marked paid here is what the practice's own books are read off. The history
   * stays readable, and every figure already in it stands.
   */
  gate: SubscriptionGate;
}

/** The one thing a row is being asked about, and the row it is being asked of. */
type PendingAction = { payment: ClientPayment; kind: "paid" | "cancel" };

/**
 * What this client has been asked for and what has come in.
 *
 * Confirming is done here because nothing reports the money in yet: a client
 * who paid by UPI, by transfer or in cash is marked off from their row, the
 * same way an enquiry's single request is marked off from its card. A request
 * that is never going to be paid — the wrong plan, the wrong price, a client
 * who changed their mind — is called off from the same row.
 */
const ClientPaymentsTab: React.FC<ClientPaymentsTabProps> = ({
  client,
  gate,
}) => {
  const toast = useToast();
  const {
    payments,
    status: loadStatus,
    error,
    request,
    markPaid,
    cancel,
  } = useClientPayments(client.id);

  // A history that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  // The two answers a row is waiting on, worked out once: every outstanding row
  // in the table wears the same one, and the tick and the cross are bare
  // buttons rather than `Button`s — see `PaymentAnswer`.
  const settleGate = gate.props("answer a payment");

  const [search, setSearch] = useState("");
  const [requesting, setRequesting] = useState(false);
  // Set while the raise is with the server, so the dialog's Confirm spins
  // rather than taking a second press and raising the request twice.
  const [sending, setSending] = useState(false);
  // The same, for the answer being given to an outstanding row below.
  const [settling, setSettling] = useState(false);
  // The row the "money is in" — or "call it off" — question is being asked
  // about. Neither is something to do by mis-clicking an icon the width of a
  // thumbnail, and the two sit next to each other.
  const [pending, setPending] = useState<PendingAction | null>(null);

  // Money in, not money asked for: a total that counted the outstanding rows
  // would say the practice has been paid for work it is still waiting on.
  const total = useMemo(
    () =>
      payments.reduce(
        (sum, payment) => (payment.status === "Paid" ? sum + payment.amount : sum),
        0
      ),
    [payments]
  );

  // Filtered here rather than by an endpoint, because the whole history is
  // already in hand — every column that reads as words is searchable, plus the
  // amount and the invoice number, which are what someone with a figure or a
  // document in front of them will type.
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return payments;

    return payments.filter((payment) =>
      [
        payment.planName,
        payment.goal,
        payment.duration,
        payment.invoiceNumber,
        String(payment.amount),
        payment.status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [payments, search]);

  /**
   * The dialog picks a plan and a price; the backend does the rest — it is
   * given the plan's id and what to charge, and answers with the whole
   * invoice, which is the row that appears at the top of the history.
   *
   * The dialog stays open until the server has it, so a request that could not
   * be raised leaves the details on screen to send again rather than closing
   * over a failure.
   */
  const handleRequest = async (payment: NewEnquiryPayment) => {
    if (sending) return;
    setSending(true);

    try {
      const raised = await request({
        planId: payment.planId,
        amount: payment.amount,
      });

      setRequesting(false);
      toast.success(
        "Payment requested",
        `Invoice ${raised.invoiceNumber} for ${formatPrice(raised.amount)} has gone to ${client.name}.`
      );
    } catch (cause) {
      toast.error(
        "Could not request this payment",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("client payment request failed:", cause);
    } finally {
      setSending(false);
    }
  };

  /**
   * What the table says when it has no rows to draw.
   *
   * A failure comes before emptiness: an empty history under "no payments yet"
   * would read as an answer, when nothing was ever heard back. A search that
   * matched nothing is its own case and a loud one — this tab is about one
   * named person, so "no payments" under a search term reads as that client
   * never having paid for anything.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState("We could not load this client’s payments");
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("payments");

    return {
      title: "No payments yet",
      description:
        "Ask this client for a payment and every request, and what came of it, is kept here.",
      action: (
        <Button
          size="sm"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setRequesting(true)}
          {...gate.props("request a payment")}
        >
          Request payment
        </Button>
      ),
    };
  };

  /** Both answers fail the same way, and the dialog stays up to be tried again. */
  const reportSettleFailure = (title: string, cause: unknown) => {
    toast.error(
      title,
      cause instanceof ApiError
        ? cause.message
        : "We could not reach the server. Check your connection and try again."
    );
    console.error(`${title.toLowerCase()}:`, cause);
  };

  /**
   * Marks the money in, on the day the dialog was told it arrived. The row is
   * replaced with what the backend answered, so the pill and the "Paid on"
   * beside it are the stored date rather than the one that was typed.
   */
  const handleConfirmPaid = async (receivedOn: string) => {
    if (!pending || settling) return;
    const { payment } = pending;
    setSettling(true);

    try {
      await markPaid(payment, receivedOn);
      setPending(null);
      toast.success(
        "Payment confirmed",
        `${formatPrice(payment.amount)} from ${client.name} has been marked as received.`
      );
    } catch (cause) {
      reportSettleFailure("Could not confirm this payment", cause);
    } finally {
      setSettling(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!pending || settling) return;
    const { payment } = pending;
    setSettling(true);

    try {
      await cancel(payment.id);
      setPending(null);
      toast.success(
        "Payment declined",
        `The ${formatPrice(payment.amount)} requested from ${client.name} has been called off.`
      );
    } catch (cause) {
      reportSettleFailure("Could not decline this payment", cause);
    } finally {
      setSettling(false);
    }
  };

  const confirmingPaid = pending?.kind === "paid";

  return (
    <div className="flex flex-col gap-4 min-h-0 flex-1">
      {/* What has come in, and the way to ask for more — the banner above the
          history, in the place the Plans tab keeps its subscription. */}
      <section className="flex-shrink-0 flex flex-wrap items-center justify-between gap-4 rounded-md border border-line bg-white px-5 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          {/* The tile carries the currency, so the figure beside it is written
              as a plain number — a second ₹ an inch away would read as two. */}
          <span
            aria-hidden="true"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#E7F8EF] text-[16px] font-medium text-[#2F8E5B]"
          >
            ₹
          </span>
          <div>
            {/* A total is a sum of rows that are not in yet, so until they
                land there is no figure to write — and a "0" here would read as
                this client having paid for nothing. A history that never
                arrived is the same: a dash, not a zero. The line is held at its
                own height either way, so nothing shifts when the number lands. */}
            {loadStatus !== "ready" ? (
              <div className="flex h-[27px] 3xl:h-[29px] items-center text-[22px] 3xl:text-[24px] font-medium text-ink-muted leading-tight">
                {loadStatus === "failed" ? (
                  "-"
                ) : (
                  // The figure's own place, held at its height and shimmered
                  // as one block once the wait is worth admitting.
                  <SkeletonCard phase={phase} className="h-5 w-[86px]" />
                )}
              </div>
            ) : (
              <div
                className="text-[22px] 3xl:text-[24px] font-medium text-ink leading-tight"
                // What is written is the number; what is read out is the money,
                // and what the number is of.
                aria-label={`${formatPrice(total)} received in total`}
              >
                {total.toLocaleString("en-IN")}
              </div>
            )}
            <p className="text-[13px] text-ink-muted">Total payment</p>
          </div>
        </div>

        <Button
          size="sm"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setRequesting(true)}
          {...gate.props("request a payment")}
        >
          New payment
        </Button>
      </section>

      <section className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 sm:px-6 pt-5 pb-4">
          <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
            Payment history
          </h2>
          <div className="relative w-full sm:w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              aria-label="Search payments"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {loadStatus === "loading" && (
          <p role="status" className="sr-only">
            Loading payment history…
          </p>
        )}

        {phase !== "ready" && visible.length === 0 ? (
          // One block the size of the table, rather than a page of blocks in
          // the shape of its cells — see `TABLE_BODY_SKELETON`.
          <div className="flex-1 min-h-0 p-4 sm:p-6">
            <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
          </div>
        ) : (
        <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
          {/* Full height only when the rows are gone — see `ClientsTable`. */}
          <table
            className={`w-full min-w-[900px] table-fixed border-collapse text-left ${
              visible.length === 0 ? "h-full" : ""
            }`}
          >
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[20%]" />
              <col className="w-[13%]" />
              <col className="w-[10%]" />
              <col className="w-[11%]" />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr>
                <th scope="col" className={headCell}>
                  Sl
                </th>
                <th scope="col" className={headCell}>
                  Plan name
                </th>
                <th scope="col" className={headCell}>
                  Goal
                </th>
                <th scope="col" className={headCell}>
                  Amount
                </th>
                <th scope="col" className={headCell}>
                  Duration
                </th>
                <th scope="col" className={headCell}>
                  Generated on
                </th>
                <th scope="col" className={headCell}>
                  Paid on
                </th>
                <th scope="col" className={headCell}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="align-middle">
                    <EmptyState {...emptyState()} />
                  </td>
                </tr>
              ) : (
                visible.map((payment, index) => {
                  const status = payment.status;

                  return (
                    <tr
                      key={payment.id}
                      className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors"
                    >
                      <td className={bodyCell}>
                        {String(index + 1).padStart(2, "0")}
                      </td>
                      <td className={`${bodyCell} truncate`}>
                        {payment.planName}
                      </td>
                      <td className={`${bodyCell} truncate`}>{payment.goal}</td>
                      <td className={bodyCell}>{formatPrice(payment.amount)}</td>
                      <td className={bodyCell}>{payment.duration}</td>
                      <td className={bodyCell}>
                        {formatDate(payment.generatedAt)}
                      </td>
                      {/* An outstanding payment has no date to show, and
                          leaving the cell blank would read as one lost. */}
                      <td className={bodyCell}>
                        {payment.paidAt ? formatDate(payment.paidAt) : "-"}
                      </td>
                      <td className={bodyCell}>
                        {/* An outstanding row is not a state to report so much
                            as a question to answer, so its cell is the two
                            answers rather than a pill saying what is already
                            plain from the empty "Paid on" beside it. A settled
                            row has nothing left to ask, and shows the pill. */}
                        {status === "Not paid" ? (
                          <div className="flex items-center gap-2">
                            {/* Said for a screen reader, which cannot see that
                                the cell holds a tick and a cross rather than a
                                word — and what the search box matches on. */}
                            <span className="sr-only">Not paid</span>
                            <PaymentAnswer
                              tone="confirm"
                              label={`Mark ${formatPrice(payment.amount)} for ${payment.planName} as received`}
                              gate={settleGate}
                              onClick={() =>
                                setPending({ payment, kind: "paid" })
                              }
                            />
                            <PaymentAnswer
                              tone="cancel"
                              label={`Decline the ${formatPrice(payment.amount)} requested for ${payment.planName}`}
                              gate={settleGate}
                              onClick={() =>
                                setPending({ payment, kind: "cancel" })
                              }
                            />
                          </div>
                        ) : (
                          <span
                            className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${STATUS_STYLES[status]}`}
                          >
                            {status}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        )}
      </section>

      {requesting && (
        <RequestPaymentModal
          recipient={`${client.salutation} ${client.name}`.trim()}
          // A blank email is a dash by the time the detail record is built.
          email={client.email === "-" ? "" : client.email}
          busy={sending}
          // Not while it is with the server: the request is going out either
          // way, and a dialog closed over it would leave nothing to say so.
          onClose={() => !sending && setRequesting(false)}
          onConfirm={handleRequest}
        />
      )}

      {/* Both are asked before the pill changes, because nothing takes either
          answer back: the money is either in or it is not, and this screen is
          the only thing that says which.

          Two dialogs rather than one with a branch in it — the confirm asks
          for a date and the cancellation asks for nothing, so what they have
          in common is only the row they are about. The confirm is the shared
          one, so this tab and the enquiry screen ask it identically. */}
      {pending && confirmingPaid && (
        <ConfirmPaymentModal
          amount={pending.payment.amount}
          payer={client.name}
          planName={pending.payment.planName}
          duration={pending.payment.duration}
          requestedAt={pending.payment.generatedAt}
          busy={settling}
          onClose={() => setPending(null)}
          onConfirm={handleConfirmPaid}
        />
      )}

      {pending && !confirmingPaid && (
        <DeclinePaymentModal
          amount={pending.payment.amount}
          payer={client.name}
          planName={pending.payment.planName}
          duration={pending.payment.duration}
          busy={settling}
          onClose={() => setPending(null)}
          onConfirm={handleCancelRequest}
        />
      )}
    </div>
  );
};

export default ClientPaymentsTab;
